import React, { useMemo, useState } from "react";
import { Row, Col, Card } from "antd";
import { Column } from "@ant-design/plots";


const TYPE_ORDER = ["Correct", "Incorrect", "Pending"];

const TYPE_COLORS = {
    Correct: "#87e8de",   // green
    Incorrect: "#ffadd2", // red
    Pending: "#ffe58f",   // orange
};

function normalizeData(data = []) {
    const typeRank = (t) => {
        const i = TYPE_ORDER.indexOf(t);
        return i === -1 ? 99 : i;
    };
    return [...data].sort((a, b) => {
        const ma = (a.market || "").toLowerCase();
        const mb = (b.market || "").toLowerCase();
        if (ma !== mb) return ma.localeCompare(mb);
        return typeRank(a.type) - typeRank(b.type);
    });
}

function sumAll(rows) {
    let total = 0;
    let byType = { Correct: 0, Incorrect: 0, Pending: 0 };
    for (const r of rows || []) {
        total += r.count || 0;
        if (r.type in byType) byType[r.type] += r.count || 0;
    }
    return { total, byType };
}

const setChartConfig = (rows, setHover) => {
    const data = normalizeData(rows || []);

    return {
        data,
        xField: "market",
        yField: "count",
        colorField: "type",
        seriesField: "type",
        isGroup: true,

        group: {
            padding: 0,
        },

        legend: { position: "bottom" },

        color: (d) => TYPE_COLORS[d.type] ?? "#91caff",

        label: {
            text: (d) => (d.count ?? 0).toLocaleString(),
            position: "top",
            style: { fontSize: 12 },
            content: (d) => (d.count ?? 0).toLocaleString(),
        },

        tooltip: {
            fields: ["market", "type", "count"],
            formatter: (d) => ({
                name: d.type,
                value: (d.count ?? 0).toLocaleString(),
            }),
        },

        style: { radiusTopLeft: 4, radiusTopRight: 4 },
        columnStyle: { radius: [4, 4, 0, 0] },

        axis: {
            x: { labelAutoHide: true, title: null },
            y: { labelFormatter: (v) => Number(v).toLocaleString(), title: null },
        },

        paddingLeft: 8,
        paddingRight: 8,

        onReady: (plot) => {
            plot.on("element:mousemove", (evt) => {
                const d = evt?.data?.data;
                if (d) setHover({ market: d.market, type: d.type, count: d.count });
            });
            plot.on("element:mouseleave", () => setHover(null));
        },
    };
};

const PredictionsByMarketStatus = ({
    data,
    isLoading,
}) => {
    const [hover, setHover] = useState(null);
    const totals = useMemo(() => sumAll(data || []), [data]);
    const config = useMemo(() => setChartConfig(data || [], setHover), [data]);

    const HeaderStat = () => {
        if (hover) {
            return (
                <div className="d-none" style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <strong>{hover.market}</strong>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                background: TYPE_COLORS[hover.type] || "#91caff",
                                display: "inline-block",
                                borderRadius: 2,
                            }}
                        />
                        {hover.type}: <b>{hover.count?.toLocaleString?.() ?? hover.count}</b>
                    </span>
                </div>
            );
        }
        
        return (
            <div className="d-none" style={{ display: "flex", gap: 16, alignItems: "center" }}>
                <span><strong>All</strong>: {totals.total.toLocaleString()}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, background: TYPE_COLORS.Correct, display: "inline-block", borderRadius: 2 }} />
                    Correct: <b>{totals.byType.Correct.toLocaleString()}</b>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, background: TYPE_COLORS.Incorrect, display: "inline-block", borderRadius: 2 }} />
                    Incorrect: <b>{totals.byType.Incorrect.toLocaleString()}</b>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, background: TYPE_COLORS.Pending, display: "inline-block", borderRadius: 2 }} />
                    Pending: <b>{totals.byType.Pending.toLocaleString()}</b>
                </span>
            </div>
        );
    };

    return (
        <Card title="Predictions by market status" extra={<HeaderStat />}>
            <Row gutter={24}>
                <Col span={24}>
                    <Column {...config} loading={isLoading} className="p-0 m-0" />
                </Col>
            </Row>
        </Card>
    );
};

export default PredictionsByMarketStatus;